// services/api/agentService.ts
import { post, get } from '../../lib/http'
import {
  useQuery,
  type QueryClient,
  type UseQueryOptions,
  keepPreviousData
} from '@tanstack/react-query'
import type {
  AgentTrendsSummaryRequest,
  AgentTrendsSummaryResponse,
  AgentTrendsCustomRequest,
  AgentTrendsRangesRequest,
  AgentTrendsOverTimeRequest,
  AgentTrendsOverTimeResponse
} from '../../types/DashboardTypes'

import type {
  LeaderboardEntry,
  LeaderboardResponse
} from '../../types/LeaderboardTypes'

const todayISO = () => new Date().toISOString().slice(0, 10)

const normalizeAgentReq = (
  r: Partial<AgentTrendsSummaryRequest>
): AgentTrendsSummaryRequest => {
  const today = todayISO()
  return {
    start_date: r.start_date ?? today,
    end_date: r.end_date ?? today,
    verbose: r.verbose ?? false,
    echo_result_columns: r.echo_result_columns ?? true
  }
}

const normalizeAgentRangesReq = (
  r: Partial<AgentTrendsRangesRequest>
): AgentTrendsRangesRequest => {
  const endDate = r.end_date ?? todayISO()
  return {
    end_date: endDate,
    range_type: r.range_type ?? '30_day',
    contact_name:
      typeof r.contact_name === 'string' ? r.contact_name.trim() : '',
    verbose: r.verbose ?? false,
    echo_result_columns: r.echo_result_columns ?? true
  }
}

const normalizeAgentCustomReq = (
  r: Partial<AgentTrendsCustomRequest>
): AgentTrendsCustomRequest => {
  const today = todayISO()
  const start = r.start_date ?? today
  const end = r.end_date ?? start
  return {
    start_date: start,
    end_date: end,
    contact_name:
      typeof r.contact_name === 'string' ? r.contact_name.trim() : '',
    verbose: r.verbose ?? false,
    echo_result_columns: r.echo_result_columns ?? true
  }
}

const normalizeAgentOverTimeReq = (
  r: Partial<AgentTrendsOverTimeRequest>
): AgentTrendsOverTimeRequest => {
  const today = new Date().toISOString().slice(0, 10)
  const start = r.start_date ?? today
  const end = r.end_date ?? start
  return {
    contact_name: (r.contact_name ?? '').trim(),
    start_date: start,
    end_date: end,
    verbose: r.verbose ?? false,
    echo_result_columns: r.echo_result_columns ?? true
  }
}

export type LeaderboardHistoryRequest = {
  start_date?: string
  end_date?: string
  contact_name?: string
  verbose?: boolean
  echo_result_columns?: boolean
}

export type LeaderboardHistoryResponse = LeaderboardResponse

const normalizeLeaderboardHistoryReq = (
  r: LeaderboardHistoryRequest
): Required<Pick<LeaderboardHistoryRequest, 'start_date' | 'end_date'>> &
  Omit<LeaderboardHistoryRequest, 'start_date' | 'end_date'> => {
  const today = todayISO()
  const start = r.start_date ?? today
  const end = r.end_date ?? start
  return {
    start_date: start,
    end_date: end,
    contact_name:
      typeof r.contact_name === 'string' ? r.contact_name.trim() : '',
    verbose: r.verbose ?? false,
    echo_result_columns: r.echo_result_columns ?? true
  }
}

/** Keys */
const agentKey = (r: AgentTrendsSummaryRequest) =>
  [
    'agentTrends',
    'summary',
    r.start_date,
    r.end_date,
    r.contact_name,
    r.verbose,
    r.echo_result_columns
  ] as const

const agentRangesKey = (r: AgentTrendsRangesRequest) =>
  [
    'agentTrends',
    'ranges',
    r.end_date,
    r.range_type,
    r.contact_name ?? '',
    r.verbose ?? false,
    r.echo_result_columns ?? true
  ] as const

const agentCustomKey = (r: AgentTrendsCustomRequest) =>
  [
    'agentTrends',
    'custom',
    r.start_date,
    r.end_date,
    r.contact_name ?? '',
    r.verbose ?? true,
    r.echo_result_columns ?? true
  ] as const

const agentOverTimeKey = (r: AgentTrendsOverTimeRequest) =>
  [
    'agentTrends',
    'overTime',
    r.contact_name,
    r.start_date,
    r.end_date,
    r.verbose ?? false,
    r.echo_result_columns ?? true
  ] as const

const leaderboardHistoryKey = (
  r: ReturnType<typeof normalizeLeaderboardHistoryReq>
) =>
  [
    'leaderboard',
    'history',
    r.start_date,
    r.end_date,
    r.contact_name ?? '',
    r.verbose ?? false,
    r.echo_result_columns ?? true
  ] as const

/** Fetchers */
export const fetchAgentTrendsSummary = (
  payload: AgentTrendsSummaryRequest,
  opts?: { token?: string }
) =>
  post<AgentTrendsSummaryResponse>('/dashboard/agent-trends-summary', payload, {
    bearerToken: opts?.token
  })

export const fetchAgentTrendsRanges = (
  payload: AgentTrendsRangesRequest,
  opts?: { token?: string }
) =>
  post<AgentTrendsSummaryResponse>(
    '/dashboard/agent-trends-summary-ranges',
    payload,
    { bearerToken: opts?.token }
  )

export const fetchAgentTrendsCustom = (
  payload: AgentTrendsCustomRequest,
  opts?: { token?: string }
) =>
  post<AgentTrendsSummaryResponse>(
    '/dashboard/agent-trends-summary-custom',
    payload,
    { bearerToken: opts?.token }
  )

export const fetchAgentTrendsOverTime = (
  payload: AgentTrendsOverTimeRequest,
  opts?: { token?: string }
) =>
  post<AgentTrendsOverTimeResponse>(
    '/dashboard/agent-trends-over-time',
    payload,
    { bearerToken: opts?.token }
  )

export const fetchLeaderboard = (opts?: { token?: string }) =>
  get<LeaderboardResponse>('/dashboard/leaderboard', {
    bearerToken: opts?.token
  })

export const fetchLeaderboardHistory = (
  payload: LeaderboardHistoryRequest,
  opts?: { token?: string }
) =>
  post<LeaderboardHistoryResponse>('/dashboard/leaderboard_history', payload, {
    bearerToken: opts?.token
  })

/** Selectors */
const selectContactNames = (res?: AgentTrendsSummaryResponse) => {
  const names = (res?.results ?? [])
    .map((r: any) => r?.contact_name)
    .filter(
      (x: unknown): x is string => typeof x === 'string' && x.trim().length > 0
    )

  const seen = new Set<string>()
  const out: string[] = []
  for (const n of names) {
    const k = n.toLowerCase()
    if (!seen.has(k)) {
      seen.add(k)
      out.push(n.trim())
    }
  }
  return out.sort((a, b) => a.localeCompare(b))
}

const AGENT_STALE = 60_000
const LB_STALE = 30_000

export function useAgentTrendsQuery(
  req: AgentTrendsSummaryRequest,
  opts?: { token?: string; enabled?: boolean }
) {
  const normalized = normalizeAgentReq(req)
  return useQuery({
    queryKey: agentKey(normalized),
    queryFn: () => fetchAgentTrendsSummary(normalized, { token: opts?.token }),
    staleTime: AGENT_STALE,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: opts?.enabled ?? true
  })
}

export function useAgentTrendsRangesQuery(
  req: AgentTrendsRangesRequest,
  opts?: { token?: string; enabled?: boolean }
) {
  const normalized = normalizeAgentRangesReq(req)
  return useQuery({
    queryKey: agentRangesKey(normalized),
    queryFn: () => fetchAgentTrendsRanges(normalized, { token: opts?.token }),
    staleTime: AGENT_STALE,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: opts?.enabled ?? true
  })
}

export function useAgentTrendsCustomQuery(
  req: AgentTrendsCustomRequest,
  opts?: { token?: string; enabled?: boolean }
) {
  const normalized = normalizeAgentCustomReq(req)
  return useQuery({
    queryKey: agentCustomKey(normalized),
    queryFn: () => fetchAgentTrendsCustom(normalized, { token: opts?.token }),
    staleTime: AGENT_STALE,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: opts?.enabled ?? true
  })
}

export function useAgentTrendsOverTimeQuery(
  req: AgentTrendsOverTimeRequest,
  opts?: { token?: string; enabled?: boolean }
) {
  const normalized = normalizeAgentOverTimeReq(req)
  return useQuery({
    queryKey: agentOverTimeKey(normalized),
    queryFn: () => fetchAgentTrendsOverTime(normalized, { token: opts?.token }),
    staleTime: AGENT_STALE,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: opts?.enabled ?? true
  })
}

export function useAgentContactNamesQuery(
  req: Partial<AgentTrendsSummaryRequest>,
  options?: Pick<
    UseQueryOptions<AgentTrendsSummaryResponse, unknown, string[]>,
    'enabled'
  >
) {
  const normalized = normalizeAgentReq(req)
  return useQuery({
    queryKey: [...agentKey(normalized), 'contactNamesOnly'],
    queryFn: () => fetchAgentTrendsSummary(normalized),
    staleTime: AGENT_STALE,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    select: res => selectContactNames(res),
    enabled: options?.enabled ?? true
  })
}


export function useLeaderboardQuery(opts?: {
  token?: string
  enabled?: boolean
  refetchIntervalMs?: number
  forceMock?: boolean
}) {
  return useQuery({
    queryKey: ['leaderboard', { forceMock: !!opts?.forceMock }],
    queryFn: async () => {
      const res = await fetchLeaderboard({ token: opts?.token })
      // if (!res?.results?.length) return MOCK_LEADERBOARD
      return res
    },
    staleTime: LB_STALE,
    refetchInterval: opts?.refetchIntervalMs ?? 30_000,
    refetchOnWindowFocus: false,
    enabled: opts?.enabled ?? true,
    select: res => {
      const sorted = [...(res.results ?? [])].sort(
        (a, b) => (b.leaderboard_score_v2 || 0) - (a.leaderboard_score_v2 || 0)
      )
      return { ...res, results: sorted }
    }
  })
}

export function useLeaderboardHistoryQuery(
  req: LeaderboardHistoryRequest,
  opts?: {
    token?: string
    enabled?: boolean
    refetchIntervalMs?: number
  }
) {
  const normalized = normalizeLeaderboardHistoryReq(req)
  return useQuery({
    queryKey: leaderboardHistoryKey(normalized),
    queryFn: () => fetchLeaderboardHistory(normalized, { token: opts?.token }),
    staleTime: LB_STALE,
    placeholderData: keepPreviousData,
    refetchInterval: opts?.refetchIntervalMs,
    refetchOnWindowFocus: false,
    enabled: opts?.enabled ?? true,
    select: res => {
      const sorted = [...(res.results ?? [])].sort(
        (a, b) => (b.leaderboard_score_v2 || 0) - (a.leaderboard_score_v2 || 0)
      )
      return { ...res, results: sorted }
    }
  })
}

/** Prefetch helpers */
export const prefetchAgentTrends = (
  qc: QueryClient,
  req: AgentTrendsSummaryRequest,
  token?: string
) => {
  const normalized = normalizeAgentReq(req)
  return qc.prefetchQuery({
    queryKey: agentKey(normalized),
    queryFn: () => fetchAgentTrendsSummary(normalized, { token }),
    staleTime: AGENT_STALE
  })
}

export const ensureAgentTrends = (
  qc: QueryClient,
  req: AgentTrendsSummaryRequest,
  token?: string
) => {
  const normalized = normalizeAgentReq(req)
  return qc.ensureQueryData({
    queryKey: agentKey(normalized),
    queryFn: () => fetchAgentTrendsSummary(normalized, { token }),
    staleTime: AGENT_STALE
  })
}

export const prefetchAgentTrendsRanges = (
  qc: QueryClient,
  req: AgentTrendsRangesRequest,
  token?: string
) => {
  const normalized = normalizeAgentRangesReq(req)
  return qc.prefetchQuery({
    queryKey: agentRangesKey(normalized),
    queryFn: () => fetchAgentTrendsRanges(normalized, { token }),
    staleTime: AGENT_STALE
  })
}

export const prefetchAgentTrendsCustom = (
  qc: QueryClient,
  req: AgentTrendsCustomRequest,
  token?: string
) => {
  const normalized = normalizeAgentCustomReq(req)
  return qc.prefetchQuery({
    queryKey: agentCustomKey(normalized),
    queryFn: () => fetchAgentTrendsCustom(normalized, { token }),
    staleTime: AGENT_STALE
  })
}

export const prefetchLeaderboardHistory = (
  qc: QueryClient,
  req: LeaderboardHistoryRequest,
  token?: string
) => {
  const normalized = normalizeLeaderboardHistoryReq(req)
  return qc.prefetchQuery({
    queryKey: leaderboardHistoryKey(normalized),
    queryFn: () => fetchLeaderboardHistory(normalized, { token }),
    staleTime: LB_STALE
  })
}

/** Invalidators */
export const invalidateAgentTrendsAll = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: ['agentTrends'] })

export const invalidateAgentTrendsSummary = (
  qc: QueryClient,
  req?: Partial<AgentTrendsSummaryRequest>
) => {
  if (req) {
    const normalized = normalizeAgentReq(req)
    return qc.invalidateQueries({ queryKey: agentKey(normalized) })
  }
  return qc.invalidateQueries({ queryKey: ['agentTrends', 'summary'] })
}

export const invalidateAgentTrendsRanges = (
  qc: QueryClient,
  req?: Partial<AgentTrendsRangesRequest>
) => {
  if (req) {
    const normalized = normalizeAgentRangesReq(req)
    return qc.invalidateQueries({ queryKey: agentRangesKey(normalized) })
  }
  return qc.invalidateQueries({ queryKey: ['agentTrends', 'ranges'] })
}

export const invalidateAgentTrendsCustom = (
  qc: QueryClient,
  req?: Partial<AgentTrendsCustomRequest>
) => {
  if (req) {
    const normalized = normalizeAgentCustomReq(req)
    return qc.invalidateQueries({ queryKey: agentCustomKey(normalized) })
  }
  return qc.invalidateQueries({ queryKey: ['agentTrends', 'custom'] })
}

export const invalidateLeaderboardHistory = (
  qc: QueryClient,
  req?: LeaderboardHistoryRequest
) => {
  if (req) {
    const normalized = normalizeLeaderboardHistoryReq(req)
    return qc.invalidateQueries({ queryKey: leaderboardHistoryKey(normalized) })
  }
  return qc.invalidateQueries({ queryKey: ['leaderboard', 'history'] })
}
